// React Components
import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Material UI Components
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Tooltip from '@mui/material/Tooltip';
import TableContainer from '@mui/material/TableContainer';

// Audit Vault Components
import ProgressBar from "../common/ProgressBar";
import AccessDenied from "../common/AccessDenied";
import ServiceIsDown from "../common/ServiceIsDown";
import PrivacyFieldSelector from './PrivacyFieldSelector';

// Audit Vault Utilities
import { verifyUserCanAccessTenantData } from "../../utilities/common-user-utils";
import { getTenantAndEnabledPrivacyFilters, getTenantPrivacyFieldDefinitions } from '../../utilities/common-tenant';
import { encryptKey } from '../../utilities/common-encrypt-util';

const WEB_API_URL = process.env.REACT_APP_WEB_API_URL;


export default function TenantPrivacyDetails(props) 
{
    const { user } = props;

    const [loading, setLoading] = useState(true);
    const [loadingMsg, setLoadingMsg] = useState("Loading ...");
    const [accessDenied, setAccessDenied] = useState(false);
    const [serviceIsDownError, setServiceIsDownError] = useState(false);
    const [tenant, setTenant] = useState('');
    const [privacyFieldsDictionary, setPrivacyFieldsDictionary] = useState('');

    // For snackbar notification UI element.  Item deleted notification.
    const [openDeletedNotification, setDeletedNotification] = useState(false); 
    const [openPrivacyPolicyDisabledNotification, setPrivacyPolicyDisabledNotification] = useState(false);  
    const [openPrivacyPolicyEnabledNotification, setPrivacyPolicyEnabledNotification] = useState(false);  
    const [openInsertedNotification, setInsertedNotification] = useState(false);  

    async function fetchData() {
        if(user)
        {
            try{                
                if (verifyUserCanAccessTenantData(user, user.tenantId))
                {
                    // Load Tenant and Tenant specific privacy filters.
                    var response = await getTenantAndEnabledPrivacyFilters(user.tenantId);                    
                    if (response) 
                    {
                        setTenant(response);  
                    }                    
                    // Load system wide privacy field definitions.
                    var response2 = await getTenantPrivacyFieldDefinitions(user.tenantId);
                    if(response2) 
                    {
                        setPrivacyFieldsDictionary(response2);
                    }
                }
                else
                {
                    setAccessDenied(true);
                }
            }
            catch (e)
            {
                setServiceIsDownError(true);
                console.log("ERROR: TenantPrivacyDetails:fetchData");
                console.log(e);
            }
            finally{
                setLoading(false);
            }
        }
    }

    useEffect(() => {
        fetchData();
    }, []);


    // Handler for when the enable or disable privacy policy button is clicked.
    const handleSetDisablePolicy = async (disablePolicy, tenantId) => {                
        try {
            var result = null;
            var data;
            var usrEmail = user.userEmail;
            if (!usrEmail) {
                usrEmail = "N/A";
            }

            // If we passed in true to handleSetDisablePolicy that means we are going to disable the policy.
            // If we passed in false to handleSetDisablePolicy that means we are going to enable the policy.
            if(disablePolicy) {
                data = {                
                    TenantId: tenantId,
                    DisablePolicy: false,
                    UserMicrosoftId: await encryptKey(user.microsoftUserId),
                    UserEmail: await encryptKey(usrEmail)
                };        
                //console.log("Disabling the privacy policy");
            }
            else {
                data = {                
                    TenantId: tenantId,
                    DisablePolicy: true,
                    UserMicrosoftId: await encryptKey(user.microsoftUserId),
                    UserEmail: await encryptKey(usrEmail)
                };        
                //console.log("Enabling the privacy policy");
            }                        
            data = JSON.stringify(data)             
            if (data) 
            {                
                result = await axios.put(`${WEB_API_URL}Tenant/UpdateTenantSetAuditLogPrivacyFilterFlag`, null, {                    
                    params: {
                        key: `${data}`
                    },
                    timeout: 600000
                });                
                if (result) {
                    // Hooks to parent to update data and the UI.                                    
                    if(disablePolicy) {
                        setPrivacyPolicyDisabledNotification(true);  // Set snackbar notification to enabled to display it.                    
                    }
                    else {
                        setPrivacyPolicyEnabledNotification(true);  // Set snackbar notification to enabled to display it.                    
                    }
                    fetchData();  // Reload Tenant and Privacy Filters.
                }
            }
        }
        catch (e) {
            console.log("ERROR: TenantPrivacyDetails.handleSetDisablePolicy");
            console.log(e);
            setServiceIsDownError(true);
        }
        finally
        {
            setLoading(false)
        }     
    }

    // Handler for when the remove privacy filter button is clicked.
    const handleRemoveFilter = async (privacyFilter) => {
        /*
        console.log("Privacy Filter Details:");
        Object.entries(privacyFilter).forEach(([key, value]) => {
          console.log(`${key}:`, value);
        });
        */
        var usrEmail = user.userEmail;
        if (!usrEmail) {
            usrEmail = "N/A";
        }

        try {
            var result = null;
            // Delete.
            var payload = {
                Id: privacyFilter.id,
                FieldId: privacyFilter.fieldId,
                CompanyId: privacyFilter.companyId,
                TenantId: privacyFilter.tenantId,
                UserMicrosoftId: await encryptKey(user.microsoftUserId),
                UserEmail: await encryptKey(usrEmail)    
            };        
            payload = JSON.stringify(payload);         
            if (payload) {                
                result = await axios.delete(`${WEB_API_URL}Tenant/DeletePrivacyFilter`, {
                    params: {
                        key: `${payload}`
                    },
                    timeout: 600000
                });                
                if (result) {
                    // Hooks to parent to update data and the UI.                                    
                    setDeletedNotification(true);  // Set snackbar notification to enabled to display it.                    
                    fetchData();  // Reload Tenant and Privacy Filters.
                }
            }
        }
        catch (e) {
            console.log("ERROR: TenantPrivacyDetails.handleRemoveFilter");
            console.log(e);
            setServiceIsDownError(true);
        }
        finally
        {
            setLoading(false)
        }
    };
          
    // Handler for when the add new privacy filter button(s) are clicked.
    const handleAddPrivacyFilter = async (auditType, fieldId) => {
        if (!tenant || !tenant.privacyFilters) {
          console.warn("Tenant or privacy filters not loaded.");
          return;
        }
        var usrEmail = user.userEmail;
        if (!usrEmail) {
            usrEmail = "N/A";
        }
      
        const existingFilters = tenant.privacyFilters[auditType] || [];
        const isDuplicate = existingFilters.some((filter) => filter.fieldId === parseInt(fieldId));
      
        if (isDuplicate) {
          console.log(`Duplicate filter: Field ID ${fieldId} already exists for audit type ${auditType}`);
          return;
        }
      
        try {
          var payload = {
            TenantId: tenant.id,
            CompanyId: tenant.companyId,
            AuditType: auditType,
            FieldId: parseInt(fieldId),
            UserMicrosoftId: await encryptKey(user.microsoftUserId),
            UserEmail: await encryptKey(usrEmail)
          };                      
          payload = JSON.stringify(payload) 
          var result = await axios.post(`${WEB_API_URL}Tenant/InsertTenantPrivacyFilter`, null, {            
            params: {
                key: `${payload}`
            },
            timeout: 600000
          });      
          if (result) {
            console.log("Privacy filter added.");
            setInsertedNotification(true);  // Set snackbar notification to enabled to display it.      
            fetchData(); // Refresh the filters
          }
          else {
            console.log("Not added.");
          }
        }
        catch (e) {
            console.log("ERROR: TenantPrivacyDetails.handleAddPrivacyFilter");
            console.log(e);
            setServiceIsDownError(true);
        }
        finally
        {
            setLoading(false)
        }
    };
      
    // For snackbar close action.
    const handleNotificationClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setDeletedNotification(false);
        setInsertedNotification(false);
        setPrivacyPolicyDisabledNotification(false);
        setPrivacyPolicyEnabledNotification(false);        
    };

    // For snackbar notification action.
    const notificationAction = (
        <React.Fragment>
            <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleNotificationClose}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </React.Fragment>
    );


    if (accessDenied)
    {
        return(<AccessDenied></AccessDenied>)
    }
    
    if (serviceIsDownError) {
        return (<><ServiceIsDown></ServiceIsDown></>);
    }

    if (tenant)
    {
        return (
            <><Card sx={{ width: '100%', maxWidth: 800, minWidth: 0 }} variant="outlined">
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <ProgressBar message={loadingMsg} loading={loading} />
                    {!loading && 
                    <>                    
                    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table sx={{
                        width: '100%',
                        minWidth: 0,
                        '& td': { overflowWrap: 'anywhere', wordBreak: 'break-word' },
                        '@media (max-width:600px)': {
                            '&, & tbody, & tr, & td': { display: 'block', width: '100% !important' },
                            '& tr': { py: 1, borderBottom: 1, borderColor: 'divider' },
                            '& td': { px: 0.5, py: 0.75, borderBottom: 0 },
                            '& td:first-of-type': { fontWeight: 600 }
                        }
                    }} aria-label="tenant privacy settings">
                    <TableBody>                    
                        <TableRow>
                            <TableCell key="privacyPolicyEnabled" style={{ width: '40%' }}><Typography>Privacy Policy Enabled:</Typography></TableCell>
                            <TableCell key="privacyPolicyEnabledValue">
                            {
                                tenant.enableAuditLogPrivacyFilter ? <TaskAltIcon fontSize="small" sx={{color: 'green'}}></TaskAltIcon> : 
                                <RadioButtonUncheckedIcon fontSize="small"></RadioButtonUncheckedIcon>
                            }
                            </TableCell>
                        </TableRow>

                        { /* Start: Only display the workload privacy filters if the audit log privacy filter is enabled. */ }
                        { tenant.enableAuditLogPrivacyFilter && ( <>
                        
                        <TableRow>
                            <TableCell key="sharepointAuditFilter" style={{ width: '50%', verticalAlign: 'top' }}>
                                <Typography>SharePoint Fields Restricted:</Typography>
                                {
                                    tenant.logSharePointAudit && <PrivacyFieldSelector auditType={0} privacyFieldsDictionary={privacyFieldsDictionary} onAdd={handleAddPrivacyFilter} />
                                }                                
                            </TableCell>
                            <TableCell key="sharePointAuditFilterValues" style={{ verticalAlign: 'top' }}>                                                 
                            {
                                // SharePoint: Audit Type = 0 
                                tenant.logSharePointAudit ? (
                                    tenant.privacyFilters && tenant.privacyFilters[0] && tenant.privacyFilters[0].length > 0 ? (
                                    <div style={{ paddingLeft: '0px' }}>
                                        {tenant.privacyFilters[0].map((filter, index) => (
                                        <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ flexGrow: 1 }}>
                                            {filter.fieldName ?? `Field ID ${filter.fieldId}`}                                            
                                            </span>                                            
                                            <Tooltip title="Remove">
                                                <DeleteIcon fontSize="small" style={{ cursor: 'pointer' }} sx={{color: 'grey'}} onClick={() => handleRemoveFilter(filter)} />                                            
                                            </Tooltip>
                                        </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Typography>None</Typography>
                                    )
                                ) : (
                                    <Typography>None - This workload is disabled</Typography>
                                )  
                            }
                            </TableCell>
                        </TableRow>                        
                        <TableRow>
                            <TableCell key="exchangeAuditFilter" style={{ width: '50%', verticalAlign: 'top'}}>
                                <Typography>Exchange Fields Restricted:</Typography>
                                {
                                    tenant.logExchangeAudit && <PrivacyFieldSelector auditType={1} privacyFieldsDictionary={privacyFieldsDictionary} onAdd={handleAddPrivacyFilter} />
                                }                                
                            </TableCell>
                            <TableCell key="exchangeAuditFilterValues" style={{ verticalAlign: 'top' }}>
                            {
                                // Exchange: Audit Type = 1 
                                tenant.logExchangeAudit ? (
                                    tenant.privacyFilters && tenant.privacyFilters[1] && tenant.privacyFilters[1].length > 0 ? (
                                    <div style={{ paddingLeft: '0px' }}>
                                        {tenant.privacyFilters[1].map((filter, index) => (
                                        <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ flexGrow: 1 }}>
                                            {filter.fieldName ?? `Field ID ${filter.fieldId}`}                                                     
                                            </span>
                                            <Tooltip title="Remove">
                                                <DeleteIcon fontSize="small" style={{ cursor: 'pointer' }} sx={{color: 'grey'}} onClick={() => handleRemoveFilter(filter)} />
                                            </Tooltip>
                                        </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Typography>None</Typography>
                                    )
                                ) : (
                                    <Typography>None - This workload is disabled</Typography>
                                )  
                            }             
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell key="entraAuditFilter" style={{ width: '50%', verticalAlign: 'top' }}>
                                <Typography>Entra ID Fields Restricted:</Typography>
                                {
                                    tenant.logEntraIDAudit && <PrivacyFieldSelector auditType={2} privacyFieldsDictionary={privacyFieldsDictionary} onAdd={handleAddPrivacyFilter} />
                                }                
                            </TableCell>
                            <TableCell key="entraAuditFilterValues" style={{ verticalAlign: 'top' }}>
                            {
                                // EntraID: Audit Type = 2
                                tenant.logEntraIDAudit ? (
                                    tenant.privacyFilters && tenant.privacyFilters[2] && tenant.privacyFilters[2].length > 0 ? (
                                    <div style={{ paddingLeft: '0px' }}>
                                        {tenant.privacyFilters[2].map((filter, index) => (
                                        <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ flexGrow: 1 }}>
                                            {filter.fieldName ?? `Field ID ${filter.fieldId}`}     
                                            </span>
                                            <Tooltip title="Remove">
                                                <DeleteIcon fontSize="small" style={{ cursor: 'pointer' }} sx={{color: 'grey'}} onClick={() => handleRemoveFilter(filter)} />
                                            </Tooltip>
                                        </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Typography>None</Typography>
                                    )
                                ) : (
                                    <Typography>None - This workload is disabled</Typography>
                                )  
                            }                                         
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell key="teamsAuditFilter" style={{ width: '50%', verticalAlign: 'top' }}>
                                <Typography>Teams Fields Restricted:</Typography>
                                {
                                    tenant.logTeamsAudit && <PrivacyFieldSelector auditType={5} privacyFieldsDictionary={privacyFieldsDictionary} onAdd={handleAddPrivacyFilter} />
                                }                                                
                            </TableCell>
                            <TableCell key="teamsAuditFilterValues" style={{ verticalAlign: 'top' }}>
                            {
                                // Teams: Audit Type = 5
                                tenant.logTeamsAudit ? (
                                    tenant.privacyFilters && tenant.privacyFilters[5] && tenant.privacyFilters[5].length > 0 ? (
                                    <div style={{ paddingLeft: '0px' }}>
                                        {tenant.privacyFilters[5].map((filter, index) => (
                                        <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ flexGrow: 1 }}>
                                            {filter.fieldName ?? `Field ID ${filter.fieldId}`}     
                                            </span>
                                            <Tooltip title="Remove">
                                                <DeleteIcon fontSize="small" style={{ cursor: 'pointer' }} sx={{color: 'grey'}} onClick={() => handleRemoveFilter(filter)} />
                                            </Tooltip>
                                        </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Typography>None</Typography>
                                    )
                                ) : (
                                    <Typography>None - This workload is disabled</Typography>
                                )  
                            }             
                            </TableCell>
                        </TableRow> 

                        </>)}
                        {/* End conditional render. */}

                    </TableBody>
                    </Table>
                    </TableContainer>
                    </>
                    }                                                                
                </CardContent>
                <CardActions sx={{ px: { xs: 1.5, sm: 2 }, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
                    <Typography component="span" sx={{ width: '100%', textAlign: { xs: 'left', sm: 'right' } }}>
                    {
                        tenant.enableAuditLogPrivacyFilter ? <Button size="small" variant="outlined" sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={(event) => handleSetDisablePolicy(true, tenant.id)}>Disable Privacy Policy</Button>:
                        <Button size="small" variant="outlined" sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={(event) => handleSetDisablePolicy(false, tenant.id)}>Enable Privacy Policy</Button>
                    }                    
                    </Typography>
                </CardActions>
            </Card>
            <div>
                <Snackbar
                    open={openDeletedNotification}
                    autoHideDuration={2000}
                    onClose={handleNotificationClose}
                    message="Privacy filter removed."
                    action={notificationAction}
                />
                <Snackbar
                    open={openInsertedNotification}
                    autoHideDuration={2000}
                    onClose={handleNotificationClose}
                    message="Privacy filter added."
                    action={notificationAction}
                />
                <Snackbar
                    open={openPrivacyPolicyDisabledNotification}
                    autoHideDuration={2000}
                    onClose={handleNotificationClose}
                    message="Privacy policy disabled for the Tenant."
                    action={notificationAction}
                />
                <Snackbar
                    open={openPrivacyPolicyEnabledNotification}
                    autoHideDuration={2000}
                    onClose={handleNotificationClose}
                    message="Privacy filter enabled for the Tenant."
                    action={notificationAction}
                />
            </div>

            </>
        );
    };

}
